import type {
    AutomationRule,
    AutomationWorkerId,
    PaymentMatchValue,
    RuleAction,
    RuleMatch,
    TransmissionMatchValue,
} from '../worker-configuration/automation/worker-automation.types';

/**
 * The rule the denials page's worker editor is building (FE-116, Jordan's v3 editor).
 *
 * ## What the editor may change, and what it must carry through untouched
 *
 * A saved rule can be richer than the editor shows. Everything the editor does not edit is kept
 * VERBATIM in `kept`, so opening a rule and saving it never quietly rewrites it:
 *
 * - **Each PAYMENT block stays its own condition group.** Blocks of one rule are ANDed, while a
 *   CARC and a RARC inside one block are alternatives (OR) — the platform's semantics, see
 *   `worker-coverage.ts`. The prototype merges a rule's blocks into one on edit, which turns
 *   "CO-252 AND M127" into "CO-252 OR M127" — a rule that fires far more widely than the one saved.
 * - **Payer, provider, facility, procedure and date blocks, and every `exclude` block**, are kept as
 *   they are. The table cannot evaluate them and the editor does not offer them.
 * - **A rejection's `messageEquals`** rides along on its block; only `contains` is edited.
 *
 * Empty lists are never written: `rarc: { codes: [] }` beside a CARC is the shape the worker's own
 * classes would read as "matches every payment", so an empty half of a block is omitted instead.
 */

export interface IPaymentConditionDraft {
    /** Full enum names, as rules store them: `CONTRACTUAL_OBLIGATIONS`. */
    groups: string[];
    carc: string[];
    rarc: string[];
}

export interface IRuleDraft {
    kind: AutomationWorkerId;
    /** Set when editing a saved rule; a new rule gets its id when built. */
    editId?: string;
    name: string;
    /** Denial rules: one entry per PAYMENT condition group. */
    payment: IPaymentConditionDraft[];
    /** Rejection rules: the phrases of the first TRANSMISSION_STATUS block. */
    contains: string[];
    /** That block's other fields (`messageEquals`), carried through. */
    transmissionRest: Record<string, unknown>;
    /** Every block the editor does not edit, verbatim. */
    kept: RuleMatch[];
    action: RuleAction;
    group?: boolean;
}

const list = (values: unknown): string[] =>
    Array.isArray(values) ? values.map(value => String(value).trim()).filter(Boolean) : [];

const unique = (values: string[]): string[] => [...new Set(values)];

export const draftFromRule = (kind: AutomationWorkerId, rule: AutomationRule): IRuleDraft => {
    const payment: IPaymentConditionDraft[] = [];
    const kept: RuleMatch[] = [];
    let contains: string[] = [];
    let transmissionRest: Record<string, unknown> = {};
    let sawTransmission = false;

    (rule.match || []).forEach(block => {
        if (kind === 'denial' && block.scope === 'PAYMENT' && !block.exclude) {
            const value = (block.value || {}) as Partial<PaymentMatchValue>;
            payment.push({
                groups: list(value.carc?.group),
                carc: list(value.carc?.codes),
                rarc: list(value.rarc?.codes),
            });
        } else if (kind === 'rejection' && block.scope === 'TRANSMISSION_STATUS' && !block.exclude && !sawTransmission) {
            sawTransmission = true;
            const { contains: phrases, ...rest } = (block.value || {}) as Partial<TransmissionMatchValue> &
                Record<string, unknown>;
            contains = list(phrases);
            transmissionRest = rest;
        } else {
            kept.push(block);
        }
    });

    return {
        kind,
        editId: rule.id,
        name: rule.name,
        payment: kind === 'denial' && payment.length === 0 ? [{ groups: [], carc: [], rarc: [] }] : payment,
        contains,
        transmissionRest,
        kept,
        action: { type: rule.action.type, value: { ...(rule.action.value || {}) } },
        group: rule.group,
    };
};

/** A new denial rule seeded from a table row: its group, its code, and its remark set. */
export const draftForDenialRow = (
    row: { group: string; reasonCode: string; remarkCodes: string[] },
    name: string,
    action: RuleAction,
): IRuleDraft => ({
    kind: 'denial',
    name,
    payment: [{ groups: [row.group], carc: [row.reasonCode], rarc: [...row.remarkCodes] }],
    contains: [],
    transmissionRest: {},
    kept: [],
    action,
});

/** A new rejection rule seeded from a message — the exact message, as the v2 dialog decided. */
export const draftForRejectionMessage = (message: string, name: string, action: RuleAction): IRuleDraft => ({
    kind: 'rejection',
    name,
    payment: [],
    contains: [message.trim()],
    transmissionRest: {},
    kept: [],
    action,
});

const paymentBlock = (condition: IPaymentConditionDraft): RuleMatch | null => {
    const groups = unique(list(condition.groups));
    const carc = unique(list(condition.carc).map(code => code.toUpperCase()));
    const rarc = unique(list(condition.rarc).map(code => code.toUpperCase()));
    if (!groups.length && !carc.length && !rarc.length) {
        return null;
    }
    const value: Partial<PaymentMatchValue> = {};
    if (groups.length || carc.length) {
        value.carc = { group: groups, codes: carc };
    }
    if (rarc.length) {
        value.rarc = { codes: rarc };
    }

    return { scope: 'PAYMENT', value: value as PaymentMatchValue };
};

export const ruleFromDraft = (draft: IRuleDraft, newId: () => string): AutomationRule => {
    const own: RuleMatch[] =
        draft.kind === 'denial'
            ? draft.payment.map(paymentBlock).filter((block): block is RuleMatch => block !== null)
            : list(draft.contains).length || Object.keys(draft.transmissionRest).length
            ? [
                  {
                      scope: 'TRANSMISSION_STATUS',
                      value: { ...draft.transmissionRest, contains: unique(list(draft.contains)) } as TransmissionMatchValue,
                  },
              ]
            : [];

    return {
        id: draft.editId || newId(),
        name: draft.name.trim(),
        match: [...own, ...draft.kept],
        action: draft.action,
        ...(draft.group !== undefined ? { group: draft.group } : { group: false }),
    };
};

/**
 * What stops the draft from being saved, in words for the form — or null. The store validates the
 * whole configuration again on save; this is the early, specific message.
 */
export const draftValidationError = (draft: IRuleDraft): string | null => {
    if (!draft.name.trim()) {
        return 'Name the rule first.';
    }
    if (draft.kind === 'denial') {
        const conditions = draft.payment.filter(c => c.groups.length || c.carc.length || c.rarc.length);
        if (!conditions.length) {
            return 'Add a CARC code or a RARC code.';
        }
        if (conditions.some(c => c.groups.length > 0 && c.carc.length === 0)) {
            // A group alone matches every adjustment in that group — most of a remittance.
            return 'A CARC group needs a CARC code.';
        }
        return null;
    }
    const exact = list((draft.transmissionRest as { messageEquals?: unknown }).messageEquals);
    return list(draft.contains).length || exact.length ? null : 'Add at least one phrase.';
};

/**
 * The worker's rules in order with the draft placed before `beforeId` (the rule shown after it), or
 * last when that is null or no longer exists. Anchored on a rule rather than an index, so a list that
 * changed between opening the editor and saving still puts the draft where the user put it.
 */
export const orderWithDraft = (
    rules: AutomationRule[],
    draft: AutomationRule,
    beforeId: string | null,
): AutomationRule[] => {
    const others = rules.filter(rule => rule.id !== draft.id);
    const index = beforeId ? others.findIndex(rule => rule.id === beforeId) : -1;

    return index < 0 ? [...others, draft] : [...others.slice(0, index), draft, ...others.slice(index)];
};
